// Deklarativní widgety (docs/10-widgety-a-pohledy.md).
// Agent v odpovědi popíše JEN to, jak data zobrazit, a odkáže na dataset z brány.
// Čísla nikdy nepíše sám: bere se výsledek nástroje, který uložila brána.
import { z } from 'zod';

export const WIDGET_LANG = 'sam-widget';

export const FORMATS = ['number', 'integer', 'hours', 'czk', 'percent', 'date', 'text'] as const;
export type Format = (typeof FORMATS)[number];
export const AGGS = ['sum', 'avg', 'count', 'min', 'max'] as const;
export type Agg = (typeof AGGS)[number];

const field = z.string().min(1).max(80);
const fmt = z.enum(FORMATS);
const scalar = z.union([z.string(), z.number(), z.boolean()]);

const base = {
  title: z.string().min(1).max(120),
  subtitle: z.string().max(200).optional(),
  /** ID datasetu z odpovědi nástroje brány (`dataset`). */
  data: z.string().min(3).max(80),
  /** Kde ve výsledku leží řádky, tečková cesta. Výchozí `rows`. */
  path: z.string().max(80).optional(),
  /** Jednoduchý filtr řádků: pole = hodnota (nebo seznam hodnot). */
  filter: z.record(z.string(), z.union([scalar, z.array(scalar)])).optional(),
  /** Formát polí, např. { "hodiny": "hours" }. */
  formats: z.record(z.string(), fmt).optional(),
};

/** Výraz pro dlaždici: `sum(hodiny)`, `count()`, `avg(x)`, `min(x)`, `max(x)`, `first(x)`, `last(x)`, `distinct(x)`. */
export const STAT_EXPR = /^(sum|avg|min|max|first|last|distinct)\(([^()]+)\)$|^count\(\)$/;

const StatsSchema = z.object({
  type: z.literal('stats'),
  ...base,
  items: z
    .array(
      z.object({
        label: z.string().min(1).max(60),
        value: z.union([z.string().regex(STAT_EXPR, 'value musí být výraz jako sum(pole) nebo count()'), z.number()]),
        format: fmt.optional(),
        hint: z.string().max(80).optional(),
      }),
    )
    .min(1)
    .max(6),
});

const SeriesSchema = z.object({
  type: z.enum(['bar', 'line', 'area']),
  ...base,
  x: field,
  y: z.union([field, z.array(field).min(1).max(6)]),
  /** Rozdělit na série podle hodnoty pole (jen s jedním y). */
  series: field.optional(),
  agg: z.enum(AGGS).optional(),
  sort: z.enum(['x', 'y', '-y', 'none']).optional(),
  limit: z.number().int().min(1).max(200).optional(),
  horizontal: z.boolean().optional(),
  stacked: z.boolean().optional(),
  format: fmt.optional(),
});

const PieSchema = z.object({
  type: z.enum(['donut', 'pie']),
  ...base,
  label: field,
  value: field,
  agg: z.enum(AGGS).optional(),
  limit: z.number().int().min(2).max(12).optional(),
  format: fmt.optional(),
});

const ProgressSchema = z.object({
  type: z.literal('progress'),
  ...base,
  label: field,
  value: field,
  /** Maximum: číslo, nebo pole s maximem pro každý řádek. Výchozí 100 (hodnota je procento). */
  max: z.union([field, z.number().positive()]).optional(),
  agg: z.enum(AGGS).optional(),
  format: fmt.optional(),
  limit: z.number().int().min(1).max(50).optional(),
});

const TableSchema = z.object({
  type: z.literal('table'),
  ...base,
  columns: z
    .array(
      z.object({
        field,
        label: z.string().max(60).optional(),
        format: fmt.optional(),
      }),
    )
    .min(1)
    .max(12)
    .optional(),
  sort: z.object({ field, dir: z.enum(['asc', 'desc']).optional() }).optional(),
  limit: z.number().int().min(1).max(500).optional(),
});

const KanbanSchema = z.object({
  type: z.literal('kanban'),
  ...base,
  column: field,
  card: field,
  detail: field.optional(),
  /** Pořadí sloupců; ostatní hodnoty se přidají za ně. */
  columns: z.array(z.string().max(60)).max(12).optional(),
  limit: z.number().int().min(1).max(200).optional(),
});

const CalendarSchema = z.object({
  type: z.literal('calendar'),
  ...base,
  /** Pole se začátkem události (datum nebo datum a čas). */
  start: field,
  /** Pole s koncem (vícedenní události). */
  end: field.optional(),
  /** Pole s názvem události. */
  label: field,
  /** Pole do tooltipu (místo, organizátor…). */
  detail: field.optional(),
  /** Pole kategorie, podle které se události obarví. */
  color: field.optional(),
  /** Navigační hlavička (měsíc a šipky). UI kit ji umí skrýt jen u velkého kalendáře. */
  header: z.boolean().optional(),
});

export const WidgetSpecSchema = z.discriminatedUnion('type', [StatsSchema, SeriesSchema, PieSchema, ProgressSchema, TableSchema, KanbanSchema, CalendarSchema]);
export type WidgetSpec = z.infer<typeof WidgetSpecSchema>;
export type WidgetType = WidgetSpec['type'];

export function parseWidget(json: string): { ok: true; spec: WidgetSpec } | { ok: false; error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return { ok: false, error: 'Popis widgetu není platný JSON.' };
  }
  const r = WidgetSpecSchema.safeParse(raw);
  if (!r.success) {
    const i = r.error.issues[0];
    return { ok: false, error: `Neplatný popis widgetu (${i.path.join('.') || 'type'}: ${i.message}).` };
  }
  return { ok: true, spec: r.data };
}

/** Bloky ```sam-widget … ``` v textu odpovědi (i nedopsané na konci). */
export function extractWidgetBlocks(markdown: string): { json: string; closed: boolean }[] {
  const out: { json: string; closed: boolean }[] = [];
  const re = /```sam-widget[^\n]*\n([\s\S]*?)(```|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(markdown))) {
    out.push({ json: m[1], closed: m[2] === '```' });
    if (!m[0].length) break;
  }
  return out;
}

/** Dataset, jak ho backend připojí ke zprávě (snímek výsledku nástroje). */
export interface DatasetSnapshot {
  id: string;
  tool: string;
  params: Record<string, unknown>;
  result: unknown;
  source: string;
  fetchedAt: string;
}

/* ---------- výpočet ---------- */

type Row = Record<string, unknown>;

export type WidgetModel =
  | { type: 'stats'; items: { label: string; value: string; hint?: string }[] }
  | { type: 'bar' | 'line' | 'area'; categories: string[]; series: { name: string; data: number[] }[]; format: Format; horizontal: boolean; stacked: boolean }
  | { type: 'donut' | 'pie'; data: { label: string; value: number }[]; total: number; format: Format }
  | { type: 'progress'; items: { label: string; value: number; max: number; pct: number; text: string }[] }
  | { type: 'table'; columns: { field: string; label: string; format: Format; numeric: boolean }[]; rows: string[][]; total: number }
  | { type: 'kanban'; columns: { name: string; cards: { title: string; detail?: string }[] }[] }
  | {
      type: 'calendar';
      header: boolean;
      /** ISO datum prvního dne s událostí (kam kalendář otevřít). */
      initial: string | null;
      events: { title: string; start: string; end: string; time: string | null; detail?: string; category?: string }[];
    };

export function getPath(obj: unknown, path: string): unknown {
  if (!path) return obj;
  return path.split('.').reduce<unknown>((o, k) => (o != null && typeof o === 'object' ? (o as Row)[k] : undefined), obj);
}

/**
 * Vnořené objekty v řádku zpřístupní i pod klíčem s tečkou (`state.label`, `reported.my_hours`),
 * aby widget šel postavit nad odpovědí zdroje bez přemapování. Pole (seznamy) se nerozbalují.
 */
export function flattenRow(r: Row, prefix = '', out: Row = {}, depth = 0): Row {
  for (const [k, v] of Object.entries(r)) {
    const key = prefix ? `${prefix}.${k}` : k;
    out[key] = v;
    if (depth < 3 && v && typeof v === 'object' && !Array.isArray(v)) flattenRow(v as Row, key, out, depth + 1);
  }
  return out;
}

export function rowsOf(result: unknown, path = 'rows'): Row[] | null {
  const v = Array.isArray(result) && path === 'rows' ? result : getPath(result, path);
  if (Array.isArray(v)) return v.filter((r): r is Row => r != null && typeof r === 'object' && !Array.isArray(r)).map((r) => flattenRow(r));
  if (v && typeof v === 'object') return [flattenRow(v as Row)];
  return null;
}

const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

const isIsoDate = (v: unknown) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(v);

function aggregate(values: unknown[], agg: Agg): number {
  if (agg === 'count') return values.length;
  const nums = values.map(num).filter((x): x is number => x !== null);
  if (!nums.length) return 0;
  if (agg === 'sum') return nums.reduce((a, b) => a + b, 0);
  if (agg === 'avg') return nums.reduce((a, b) => a + b, 0) / nums.length;
  if (agg === 'min') return Math.min(...nums);
  return Math.max(...nums);
}

function groupBy(rows: Row[], key: string): Map<string, Row[]> {
  const m = new Map<string, Row[]>();
  for (const r of rows) {
    const k = r[key] == null ? '–' : String(r[key]);
    const list = m.get(k);
    if (list) list.push(r);
    else m.set(k, [r]);
  }
  return m;
}

const round = (n: number) => Math.round(n * 100) / 100;

export function formatValue(v: unknown, f: Format = 'number'): string {
  if (v == null || v === '') return '–';
  if (f === 'text') return String(v);
  if (f === 'date' || (isIsoDate(v) && f === 'number' && typeof v === 'string')) {
    if (!isIsoDate(v)) return String(v);
    const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
    return `${d}. ${m}.${y !== new Date().getFullYear() ? ` ${y}` : ''}`;
  }
  const n = num(v);
  if (n === null) return String(v);
  const cs = (x: number, max = 2) => x.toLocaleString('cs-CZ', { maximumFractionDigits: max });
  switch (f) {
    case 'integer':
      return cs(Math.round(n), 0);
    case 'hours':
      return `${cs(n, 1)} h`;
    case 'percent':
      return `${cs(n, 1)} %`;
    case 'czk':
      return Math.abs(n) >= 1_000_000 ? `${cs(n / 1_000_000, 2)} mil. Kč` : `${cs(n, 0)} Kč`;
    default:
      return cs(n);
  }
}

/** Formát pole: z `formats`, jinak odhad (datum, text). */
function fieldFormat(spec: WidgetSpec, f: string, sample?: unknown): Format {
  const given = spec.formats?.[f];
  if (given) return given;
  if (isIsoDate(sample)) return 'date';
  if (sample != null && num(sample) === null) return 'text';
  return 'number';
}

function applyFilter(rows: Row[], filter: WidgetSpec['filter']): Row[] {
  if (!filter) return rows;
  return rows.filter((r) =>
    Object.entries(filter).every(([k, want]) => {
      const v = r[k];
      const list = Array.isArray(want) ? want : [want];
      return list.some((w) => String(w) === String(v));
    }),
  );
}

function evalStat(expr: string | number, rows: Row[]): { value: number | string | null; field?: string } {
  if (typeof expr === 'number') return { value: expr };
  if (expr === 'count()') return { value: rows.length };
  const m = expr.match(STAT_EXPR)!;
  const fn = m[1];
  const f = m[2].trim();
  const vals = rows.map((r) => r[f]);
  if (fn === 'first') return { value: (vals[0] as number | string) ?? null, field: f };
  if (fn === 'last') return { value: (vals[vals.length - 1] as number | string) ?? null, field: f };
  if (fn === 'distinct') return { value: new Set(vals.map(String)).size, field: f };
  return { value: aggregate(vals, fn as Agg), field: f };
}

export function computeWidget(spec: WidgetSpec, result: unknown): { ok: true; model: WidgetModel; rowCount: number } | { ok: false; error: string } {
  const all = rowsOf(result, spec.path ?? 'rows');
  if (!all) return { ok: false, error: `Ve výsledku nejsou řádky (${spec.path ?? 'rows'}).` };
  const rows = applyFilter(all, spec.filter);
  const sample = (f: string) => rows.find((r) => r[f] != null)?.[f];

  switch (spec.type) {
    case 'stats': {
      const items = spec.items.map((it) => {
        const { value, field: f } = evalStat(it.value, rows);
        const format = it.format ?? (f ? spec.formats?.[f] : undefined) ?? (typeof value === 'string' && isIsoDate(value) ? 'date' : 'number');
        return { label: it.label, value: formatValue(typeof value === 'number' ? round(value) : value, format), hint: it.hint };
      });
      return { ok: true, model: { type: 'stats', items }, rowCount: rows.length };
    }
    case 'bar':
    case 'line':
    case 'area': {
      const ys = Array.isArray(spec.y) ? spec.y : [spec.y];
      const agg = spec.agg ?? 'sum';
      const groups = groupBy(rows, spec.x);
      let cats = [...groups.keys()];
      let series: { name: string; data: number[] }[];
      if (spec.series && ys.length === 1) {
        const names = [...new Set(rows.map((r) => String(r[spec.series!] ?? '–')))].slice(0, 8);
        series = names.map((name) => ({
          name,
          data: cats.map((c) => round(aggregate(groups.get(c)!.filter((r) => String(r[spec.series!] ?? '–') === name).map((r) => r[ys[0]]), agg))),
        }));
      } else {
        series = ys.map((y) => ({ name: y, data: cats.map((c) => round(aggregate(groups.get(c)!.map((r) => r[y]), agg))) }));
      }
      const sort = spec.sort ?? (isIsoDate(cats[0]) || spec.type !== 'bar' ? 'x' : '-y');
      let order = cats.map((_, i) => i);
      const totals = cats.map((_, i) => series.reduce((s, se) => s + se.data[i], 0));
      if (sort === 'x') order.sort((a, b) => cats[a].localeCompare(cats[b], 'cs', { numeric: true }));
      else if (sort === 'y') order.sort((a, b) => totals[a] - totals[b]);
      else if (sort === '-y') order.sort((a, b) => totals[b] - totals[a]);
      if (spec.limit) order = order.slice(0, spec.limit);
      const xFmt = fieldFormat(spec, spec.x, cats[0]);
      cats = order.map((i) => (xFmt === 'date' ? formatValue(cats[i], 'date') : cats[i]));
      series = series.map((s) => ({ ...s, data: order.map((i) => s.data[i]) }));
      const format = spec.format ?? spec.formats?.[ys[0]] ?? (agg === 'count' ? 'integer' : 'number');
      return {
        ok: true,
        model: { type: spec.type, categories: cats, series, format, horizontal: !!spec.horizontal, stacked: !!spec.stacked },
        rowCount: rows.length,
      };
    }
    case 'donut':
    case 'pie': {
      const agg = spec.agg ?? 'sum';
      const groups = groupBy(rows, spec.label);
      let data = [...groups.entries()].map(([label, rs]) => ({ label, value: round(aggregate(rs.map((r) => r[spec.value]), agg)) }));
      data.sort((a, b) => b.value - a.value);
      const limit = spec.limit ?? 6;
      if (data.length > limit) {
        const rest = data.slice(limit - 1).reduce((s, d) => s + d.value, 0);
        data = [...data.slice(0, limit - 1), { label: 'Ostatní', value: round(rest) }];
      }
      data = data.filter((d) => d.value > 0);
      const format = spec.format ?? spec.formats?.[spec.value] ?? (agg === 'count' ? 'integer' : 'number');
      return { ok: true, model: { type: spec.type, data, total: round(data.reduce((s, d) => s + d.value, 0)), format }, rowCount: rows.length };
    }
    case 'progress': {
      const agg = spec.agg ?? 'sum';
      const groups = groupBy(rows, spec.label);
      let items = [...groups.entries()].map(([label, rs]) => {
        const value = aggregate(rs.map((r) => r[spec.value]), agg);
        const max = typeof spec.max === 'number' ? spec.max : spec.max ? aggregate(rs.map((r) => r[spec.max as string]), agg) : 100;
        const pct = max > 0 ? (value / max) * 100 : 0;
        const format = spec.format ?? spec.formats?.[spec.value] ?? (spec.max ? 'number' : 'percent');
        const text = spec.max ? `${formatValue(round(value), format)} / ${formatValue(round(max), format)}` : formatValue(round(value), format);
        return { label, value: round(value), max: round(max), pct: round(pct), text };
      });
      if (spec.limit) items = items.slice(0, spec.limit);
      return { ok: true, model: { type: 'progress', items }, rowCount: rows.length };
    }
    case 'table': {
      const fields = spec.columns?.map((c) => c.field) ?? Object.keys(rows[0] ?? {}).filter((k) => {
        const v = rows[0]?.[k];
        return v == null || typeof v !== 'object';
      });
      const columns = fields.map((f) => {
        const c = spec.columns?.find((x) => x.field === f);
        const format = c?.format ?? fieldFormat(spec, f, sample(f));
        return { field: f, label: c?.label ?? f, format, numeric: !['text', 'date'].includes(format) };
      });
      let sorted = rows;
      if (spec.sort) {
        const { field: f, dir = 'asc' } = spec.sort;
        sorted = [...rows].sort((a, b) => {
          const x = a[f];
          const y = b[f];
          const nx = num(x);
          const ny = num(y);
          const c = nx !== null && ny !== null ? nx - ny : String(x ?? '').localeCompare(String(y ?? ''), 'cs', { numeric: true });
          return dir === 'desc' ? -c : c;
        });
      }
      const limit = spec.limit ?? 100;
      const body = sorted.slice(0, limit).map((r) => columns.map((c) => formatValue(r[c.field], c.format)));
      return { ok: true, model: { type: 'table', columns, rows: body, total: rows.length }, rowCount: rows.length };
    }
    case 'kanban': {
      const groups = groupBy(rows, spec.column);
      const names = [...(spec.columns ?? []), ...[...groups.keys()].filter((k) => !spec.columns?.includes(k))];
      const limit = spec.limit ?? 50;
      const columns = names.map((name) => ({
        name,
        cards: (groups.get(name) ?? []).slice(0, limit).map((r) => ({
          title: String(r[spec.card] ?? '–'),
          detail: spec.detail ? formatValue(r[spec.detail], fieldFormat(spec, spec.detail, r[spec.detail])) : undefined,
        })),
      }));
      return { ok: true, model: { type: 'kanban', columns }, rowCount: rows.length };
    }
    case 'calendar': {
      const hm = (v: string) => (/T\d{2}:\d{2}/.test(v) ? v.slice(11, 16).replace(/^0/, '') : null);
      const events = rows
        .filter((r) => isIsoDate(r[spec.start]))
        .map((r) => {
          const start = String(r[spec.start]);
          const end = spec.end && isIsoDate(r[spec.end]) ? String(r[spec.end]) : start;
          const t0 = hm(start);
          const t1 = hm(end);
          return {
            title: String(r[spec.label] ?? '–'),
            start,
            end,
            time: t0 ? (t1 && t1 !== t0 ? `${t0}–${t1}` : t0) : null,
            detail: spec.detail && r[spec.detail] != null ? String(r[spec.detail]) : undefined,
            category: spec.color && r[spec.color] != null ? String(r[spec.color]) : undefined,
          };
        })
        .sort((a, b) => a.start.localeCompare(b.start));
      return {
        ok: true,
        model: { type: 'calendar', header: spec.header ?? true, initial: events[0]?.start.slice(0, 10) ?? null, events },
        rowCount: rows.length,
      };
    }
  }
}
